import { Injectable, Injector } from '@angular/core';
import { CreateCorporateLicenceDto, CorporateLicenceDto, CorporateLicenceServiceProxy, UpdateCorporateLicenceDto, GetCorporateLicencesOutput, CorporateLicenceType, GetEmploymentDto, CreateCorporateLicenceRenewalDto, UpdateCorporateLicenceRenewalDto, SearchCorporateLicencesInputDto, SearchCorporateLicencesOutputDto, CorporateLicenceHistoryDto } from '@shared/service-proxies/service-proxies';
import { map, Observable, ReplaySubject, Subject, tap } from 'rxjs';
import { SponsorshipDossierService } from './sponsorship-dossier.service';
import { AppConsts } from '@shared/AppConsts';
import { SelectItem } from 'primeng/api';
import { DatePipe } from '@angular/common';
import { AppComponentBase } from '@shared/common/app-component-base';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class CorporateLicenceService extends AppComponentBase {

    private corporateLicencesForSponsorshipSubject = new ReplaySubject<CorporateLicenceDto[]>(1);
    readonly corporateLicencesForSponsorship$ = this.corporateLicencesForSponsorshipSubject.asObservable();
    private corporateLicenceRenewalsChangedSubject = new Subject<void>();
    readonly corporateLicenceRenewalsChanged$ = this.corporateLicenceRenewalsChangedSubject.asObservable();

    id: number;

    constructor(
        injector: Injector,
        protected datePipe: DatePipe,
        private corporateLicenceServiceProxy: CorporateLicenceServiceProxy,
        private sponsorshipDossierService: SponsorshipDossierService) {
        super(injector);
    }

    public setId(id: number) {
        this.id = id;
    }

    public getId() {
        return this.id;
    }

    public getSponsorshipId(){
        let sponsorshipId = this.sponsorshipDossierService.GetId();
        return sponsorshipId;
    }

    public isActive() {
        return this.id > 0;
    }

    public get(): Observable<CorporateLicenceDto> {
        return this.corporateLicenceServiceProxy.get(this.id);
    }

    public createWithInvoiceAddress(input: CreateCorporateLicenceDto): Observable<number> {
        return this.corporateLicenceServiceProxy.create(input).pipe(
            tap(() => this.loadCorporateLicencesForSponsorship().subscribe())
        );
    }

    public createRenewal(input: CreateCorporateLicenceRenewalDto): Observable<number> {
        return this.corporateLicenceServiceProxy.createRenewal(input).pipe(
            tap(() => {
                this.loadCorporateLicencesForSponsorship().subscribe();
                this.corporateLicenceRenewalsChangedSubject.next();
            })
        );
    }

    public updateRenewal(id: number, input: UpdateCorporateLicenceRenewalDto): Observable<void> {
        return this.corporateLicenceServiceProxy.updateRenewal(id, input);
    }

    public update(input: UpdateCorporateLicenceDto): Observable<void> {
        return this.corporateLicenceServiceProxy.update(this.getId(), input).pipe(
            tap(() => this.loadCorporateLicencesForSponsorship().subscribe())
        );
    }

    public delete(): Observable<void> {
        return this.corporateLicenceServiceProxy.delete(this.getId()).pipe(
            tap(() => this.loadCorporateLicencesForSponsorship().subscribe())
        );
    }

    public getAssistantAndDelegatedLicencesForAssistant(personId: number): Observable<CorporateLicenceDto[]> {
        return this.corporateLicenceServiceProxy.getAssistantAndDelegatedLicencesForAssistantPerson(personId);
    }

    public loadCorporateLicencesForSponsorship(sponsorshipId: number = this.sponsorshipDossierService.GetId()): Observable<GetCorporateLicencesOutput> {
        return this.corporateLicenceServiceProxy.getCorporateLicences(sponsorshipId).pipe(
            tap(x => this.corporateLicencesForSponsorshipSubject.next(x.items))
        );
    }

    public clearCorporateLicencesForSponsorship() {
        this.corporateLicencesForSponsorshipSubject.next([]);
    }

    public getCorporateLicencesForBusiness(businessId: number): Observable<GetCorporateLicencesOutput> {
        return this.corporateLicenceServiceProxy.getCorporateLicencesForBusiness(businessId);
    }

    public searchCorporateLicences(sponsorshipId: number, personId: number, businessId: number, types: CorporateLicenceType[], sorting: string, filter: string, skipCount: number, maxResultCount: number): Observable<SearchCorporateLicencesOutputDto> {
        return this.corporateLicenceServiceProxy.searchCorporateLicences(new SearchCorporateLicencesInputDto(
        {
            sponsorshipId: sponsorshipId,
            personId: personId,
            types: types,
            sorting: sorting,
            filter: filter,
            skipCount: skipCount,
            maxResultCount: maxResultCount,
            businessId: businessId
        }));
    }

    public createLicenceTypeDropdownList(licenceTypes: CorporateLicenceType[]): SelectItem[] {
        let dropdownList: SelectItem[] = [];
        licenceTypes.forEach(licenceType => {
                dropdownList.push({ value: licenceType, label:  this.l('CorporateLicenceType' + '_' + CorporateLicenceType[licenceType]) });
        });
        return dropdownList;
    }

    createLicenceDropdownList(): Observable<SelectItem[]> {

        return this.corporateLicencesForSponsorship$.pipe(map(items => {
            const dropdownList: SelectItem[] = [];
            items.forEach(licence => {
                dropdownList.push({ value: licence.id, label: this.getTitle(licence.typeId, false) + ': ' + this.l(licence.sponsorshipTypeId) + ', ' + this.l('Status') + ': ' + this.l(licence.statusId) + ', ' + (licence.dateOfIssue != null ? this.l('LabelDateOfIssue') + ': ' + this.datePipe.transform(licence.dateOfIssue.toJSDate()) : '') });
            });
            return dropdownList;
        }));
    }

    createLicenceDropdownListFilteredBySponsorshipType(sponsorshipTypeId: string): Observable<SelectItem[]> {
        const allowedTypes = this.getCorporateLicenceTypesForSponsorshipType(sponsorshipTypeId);
        return this.corporateLicencesForSponsorship$.pipe(map(items => {
            const dropdownList: SelectItem[] = [];
            items.filter(x => allowedTypes.includes(x.typeId)).forEach(licence => {
                dropdownList.push({ value: licence.id, label: this.getTitle(licence.typeId, false) + ': ' + this.l(licence.sponsorshipTypeId) + ', ' + this.l('Status') + ': ' + this.l(licence.statusId) + ', ' + (licence.dateOfIssue != null ? this.l('LabelDateOfIssue') + ': ' + this.datePipe.transform(licence.dateOfIssue.toJSDate()) : '') });
            });
            return dropdownList;
        }));
    }

    createMedicalFieldDirectorDropdownList(employments: GetEmploymentDto[]): SelectItem[] {
        const dropdownList: SelectItem[] = [];
        employments.filter(x => x.roleId === this.codes.employmentRole.medicalFieldResponsible &&
                            (x.corporateLicenceId === this.id || x.corporateLicenceId == null)).forEach(employment => {
            if ((employment.dateOfEmployment === undefined || employment.dateOfEmployment < DateTime.now()) &&
                (employment.dateOfEndOfEmployment === undefined || employment.dateOfEndOfEmployment > DateTime.now())) {
                dropdownList.push(this.createDropdownEntryFromEmployment(employment));
            }
        });
        return dropdownList;
    }

    createMedicalFieldResponsibleForStorageAndQualityDropdownList(employments: GetEmploymentDto[]): SelectItem[] {
        const dropdownList: SelectItem[] = [];
        employments.filter(x => x.roleId === this.codes.employmentRole.medicalFieldResponsibleForStorageAndQuality && (x.corporateLicenceId === this.id || x.corporateLicenceId == null)).forEach(employment => {
            dropdownList.push(this.createDropdownEntryFromEmployment(employment));
        });
        return dropdownList;
    }

    createMedicalFieldResponsibleForHaemovigilanceDropdownList(employments: GetEmploymentDto[]): SelectItem[] {
        const dropdownList: SelectItem[] = [];
        employments.filter(x => x.roleId === this.codes.employmentRole.medicalFieldResponsibleForHaemovigilance && (x.corporateLicenceId === this.id || x.corporateLicenceId == null)).forEach(employment => {
            dropdownList.push(this.createDropdownEntryFromEmployment(employment));
        });
        return dropdownList;
    }

    createDropdownEntryFromEmployment(employment: GetEmploymentDto): SelectItem {
        let label = employment.person != null ? employment.person.firstName + ' ' + employment.person.lastName : employment.personLicence?.personId != null ? employment.personLicence?.person.firstName + ' ' + employment.personLicence?.person.lastName : this.l('NoPersonAssigned');
        label = label + ' (' + this.l('EntryOn') + ': ' + (employment.dateOfEmployment ? this.datePipe.transform(employment.dateOfEmployment.toJSDate()) : '-');
        label = label + (label.length > 0 ? ', ' : '');
        label = label + this.l('Status') + ': ' + this.l(employment.statusId) + ')';
        return { value: employment.id, label: label };
    }

    public IsLicenceInFinalState(statusId: string): boolean {
        return statusId !== this.codes.professionalLicenceStatus.requested;
    }

    public getCssClassForClaStatusId(statusId: string, prefix: string): string {
        if (statusId === AppConsts.codes.professionalLicenceStatus.granted ||
            statusId === AppConsts.codes.professionalLicenceStatus.inactive) {
            return prefix + 'success';
        } else if (statusId === AppConsts.codes.professionalLicenceStatus.banned ||
            statusId === AppConsts.codes.professionalLicenceStatus.refused ||
            statusId === AppConsts.codes.professionalLicenceStatus.revoked) {
            return prefix + 'danger';
        } else if (statusId === AppConsts.codes.professionalLicenceStatus.restricted) {
            return prefix + 'warning';
        }

        return prefix + 'secondary';
    }

    public getTitle(licenceType: CorporateLicenceType, isRenewal: boolean, long = false): string {
        const suffix = long ? '-long' : '';

        if (isRenewal === true) {
            switch (licenceType) {
                case CorporateLicenceType.CorporateLicence: return this.l('CorporateLicenceType_CorporateLicenceRenewal' + suffix);
                case CorporateLicenceType.LocalSalesLicence: return this.l('CorporateLicenceType_LocalSalesLicenceRenewal' + suffix);
                case CorporateLicenceType.OnlineSalesLicence: return this.l('CorporateLicenceType_OnlineSalesLicenceRenewal' + suffix);
                case CorporateLicenceType.DelegatedLicence: return this.l('CorporateLicenceType_DelegatedLicenceRenewal' + suffix);
                case CorporateLicenceType.NarcoticLicence: return this.l('CorporateLicenceType_NarcoticLicenceRenewal' + suffix);
                case CorporateLicenceType.BloodstorageLicence: return this.l('CorporateLicenceType_BloodstorageLicenceRenewal' + suffix);
                case CorporateLicenceType.ExpenseHealthCareLicence: return this.l('CorporateLicenceType_ExpenseHealthCareLicenceRenewal' + suffix);
                case CorporateLicenceType.ManufacturingLicence: return this.l('CorporateLicenceType_ManufacturingLicenceRenewal' + suffix);
            }
        }

        switch (licenceType) {
            case CorporateLicenceType.CorporateLicence: return this.l('CorporateLicenceType_CorporateLicence' + suffix);
            case CorporateLicenceType.LocalSalesLicence: return this.l('CorporateLicenceType_LocalSalesLicence' + suffix);
            case CorporateLicenceType.OnlineSalesLicence: return this.l('CorporateLicenceType_OnlineSalesLicence' + suffix);
            case CorporateLicenceType.AssistantLicence: return this.l('CorporateLicenceType_AssistantLicence' + suffix);
            case CorporateLicenceType.DelegatedLicence: return this.l('CorporateLicenceType_DelegatedLicence' + suffix);
            case CorporateLicenceType.NarcoticLicence: return this.l('CorporateLicenceType_NarcoticLicence' + suffix);
            case CorporateLicenceType.BloodstorageLicence: return this.l('CorporateLicenceType_BloodstorageLicence' + suffix);
            case CorporateLicenceType.ExpenseHealthCareLicence: return this.l('CorporateLicenceType_ExpenseHealthCareLicence' + suffix);
            case CorporateLicenceType.ManufacturingLicence: return this.l('CorporateLicenceType_ManufacturingLicence' + suffix);
            case CorporateLicenceType.OtherLicence: return this.l('CorporateLicenceType_OtherLicence' + suffix);
        }
    }

    public getCorporateLicenceTypesForSponsorshipType(sponsorshipTypeId: string): CorporateLicenceType[] {
        switch (sponsorshipTypeId) {
            case AppConsts.codes.sponsorshipType.outpatientPhysicianClinic:
            case AppConsts.codes.sponsorshipType.outpatientPhysicianPolyclinic:
            case AppConsts.codes.sponsorshipType.outpatientDentalClinic:
            case AppConsts.codes.sponsorshipType.outpatientDentalPolyclinic:
            case AppConsts.codes.sponsorshipType.hospital:
                return [CorporateLicenceType.CorporateLicence, CorporateLicenceType.LocalSalesLicence];
            case AppConsts.codes.sponsorshipType.outpatientChiropractorClinic:
                return [CorporateLicenceType.CorporateLicence];
            case AppConsts.codes.sponsorshipType.publicPharmacy:
                return [CorporateLicenceType.LocalSalesLicence, CorporateLicenceType.OnlineSalesLicence];
            default:
                return [];
        }
    }

    public getHistory(): Observable<CorporateLicenceHistoryDto[]> {
        return this.corporateLicenceServiceProxy.getHistory(this.id);
    }
}
